import { useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { Loader2, Check, X, Trash2 } from 'lucide-react';

const small = `rounded-lg border border-slate-700 bg-slate-900 px-2 py-1.5 text-xs text-white
               placeholder-slate-500 focus:outline-none focus:border-amber-400/70`;

/** One change across every card he ticked — so fixing 47 is one action, not 47 visits. */
export const RitualBulkBar = ({ ids, categories, subcategories, onDone, onClear }) => {
  const [changes, setChanges] = useState({});
  const [busy, setBusy] = useState(false);

  const set = (key) => (e) => {
    const value = e.target.value;
    setChanges((prev) => {
      const next = { ...prev };
      if (value === '') delete next[key];
      else next[key] = key === 'active' ? value === 'true' : value;
      return next;
    });
  };

  const apply = async () => {
    if (Object.keys(changes).length === 0) return toast.error('Nothing to change');
    setBusy(true);
    try {
      const { data } = await axios.patch(`${API}/admin/ritual-cards/bulk`, { ids, changes });
      toast.success(`${data.updated} card${data.updated === 1 ? '' : 's'} updated`);
      setChanges({});
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Those cards could not be updated');
    } finally {
      setBusy(false);
    }
  };

  const removeAll = async () => {
    if (!window.confirm(`Delete ${ids.length} cards? Members who adopted them keep their own copy.`)) return;
    setBusy(true);
    await Promise.all(ids.map((id) => axios.delete(`${API}/admin/ritual-cards/${id}`).catch(() => {})));
    setBusy(false);
    toast.success(`${ids.length} cards deleted`);
    onDone();
  };

  return (
    <div className="rounded-xl border border-amber-400/40 bg-amber-400/5 p-3 space-y-2"
         data-testid="ritual-bulk-bar">
      <div className="flex items-center justify-between">
        <p className="text-xs text-amber-100">
          <span className="font-semibold">{ids.length}</span> ticked
        </p>
        <button onClick={onClear} className="text-slate-400 hover:text-white transition-colors"
                data-testid="bulk-clear-btn">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <input className={small} placeholder="Family" list="bulk-categories"
               value={changes.category ?? ''} onChange={set('category')}
               data-testid="bulk-category-input" />
        <datalist id="bulk-categories">
          {categories.map((name) => <option key={name} value={name} />)}
        </datalist>
        <input className={small} placeholder="Sub-category" list="bulk-subcategories"
               value={changes.subcategory ?? ''} onChange={set('subcategory')}
               data-testid="bulk-subcategory-input" />
        <datalist id="bulk-subcategories">
          {subcategories.map((name) => <option key={name} value={name} />)}
        </datalist>
        <select className={small} value={changes.suggested_window ?? ''}
                onChange={set('suggested_window')} data-testid="bulk-window-select">
          <option value="">Window unchanged</option>
          <option value="any">Any window</option>
          <option value="morning">Morning</option>
          <option value="midday">Midday</option>
          <option value="evening">Evening</option>
        </select>
        <select className={small} value={changes.active ?? ''} onChange={set('active')}
                data-testid="bulk-active-select">
          <option value="">Visibility unchanged</option>
          <option value="true">Live</option>
          <option value="false">Hidden</option>
        </select>
      </div>

      <div className="flex gap-2">
        <button onClick={apply} disabled={busy}
                className="inline-flex items-center gap-1.5 rounded-lg bg-amber-500 px-3 py-1.5
                           text-xs font-semibold text-slate-950 hover:bg-amber-400
                           disabled:opacity-40 transition-colors"
                data-testid="bulk-apply-btn">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
          Apply to {ids.length}
        </button>
        <button onClick={removeAll} disabled={busy}
                className="inline-flex items-center gap-1.5 rounded-lg border border-rose-500/40
                           px-3 py-1.5 text-xs text-rose-200 hover:border-rose-400
                           disabled:opacity-40 transition-colors"
                data-testid="bulk-delete-btn">
          <Trash2 className="w-3.5 h-3.5" /> Delete
        </button>
      </div>
    </div>
  );
};

export default RitualBulkBar;
